import Page from 'components/common/page';
import titleTemplate from 'lib/title-template';
import { sendResetPasswordEmail } from 'models/auth-model';
import type { NextPage } from 'next';
import Head from 'next/head';
import { type MouseEvent, useState } from 'react';
import Floating from 'components/common/floating';
import Button from 'components/common/button';
import axios from 'axios';
import cx from 'classnames';

type RequestState = 'idle' | 'in progress' | 'failed' | 'success';

const ForgotPassword: NextPage = () => {
  const [email, setEmail] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [state, setState] = useState<RequestState>('idle');

  const handleSubmit = async (event: MouseEvent): Promise<void> => {
    event.preventDefault();
    if (!email) {
      return;
    }
    setMessage(null);
    setState('in progress');
    try {
      await sendResetPasswordEmail(email);
      setState('success');
      setMessage(
        `If an account exists, you will get an email with instructions on resetting your password. If it doesn't arrive, be sure to check your spam folder.`,
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setMessage(error.response?.data?.message || error.message);
      }
      // Should set state as failed in any error case?
      setState('failed');
    }
  };

  return (
    <Page pageName="Forgot Password">
      <Head>
        <title>{titleTemplate('Forgot password')}</title>
      </Head>
      <meta
        name="description"
        content="Forgot your password? We’re here to help. Just type your email address and we’ll reset your password so you can continue using Bibcitation."
      />
      <div className="px-4">
        <Floating>
          {state !== 'success' && (
            <h1 className="font-display text-[1.75rem] leading-tight font-semibold tracking-[-0.01em] text-ink">
              Forgot password?
            </h1>
          )}
          {message && (
            <div
              role={state === 'failed' ? 'alert' : 'status'}
              className={cx('rounded-xl px-4 py-3 text-15 leading-normal', {
                'bg-ok-soft text-ok': state === 'success',
                'mt-5 bg-danger-soft text-danger': state === 'failed',
              })}
            >
              {message}
            </div>
          )}
          {state !== 'success' && (
            <form>
              <label
                className="mt-5 mb-1.5 block text-sm font-semibold text-ink"
                htmlFor="email"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                onChange={(event) => setEmail(event.target.value)}
              />
              <div className="mt-6">
                <Button fullWidth type="brand" onClick={handleSubmit}>
                  Reset password
                </Button>
              </div>
            </form>
          )}
        </Floating>
      </div>
    </Page>
  );
};

export default ForgotPassword;
